/**
 * Show/hide logic for the e-form page — a pure port of FTP's `performLogic`
 * (FTP: html/forms_survey_body_mobile.php, the generated `selectSurveyItemID<N>()`
 * functions). Works for any question, page breaks (types 13/20) included: for a
 * page break the result says whether that page is active.
 *
 * How FTP decides, and how this module mirrors it:
 *
 * - Groups. `performLogic` walks the questions in form order; for each one it
 *   walks the rules whose source is that question and applies the rule's whole
 *   group to the group's target (show → visible when met, hidden when not;
 *   hide → the reverse). Every group is applied and the last one applied wins.
 *   We replay that order: by source-question position, then rule ID (FTP reads
 *   the rules without ORDER BY, i.e. primary-key order). A target that no group
 *   reaches stays visible, as FTP's page shows every item before running logic.
 *   (While a patient answers, FTP re-runs only the changed source's groups, so
 *   its state can depend on click history; we always give the full-run answer.)
 *
 * - all / any. A group is met when "all": every rule that FTP evaluates passes
 *   and at least one does; "any": at least one passes. Rules with no source and
 *   no option (FTP skips `targetItemID == 0 && surveysOptionID == 0`), and rules
 *   whose source type FTP has no branch for, are not evaluated at all.
 *
 * - Rule per source type (the rule's own source; FTP used the type of the
 *   source that triggered the group — only differs for groups that mix types):
 *   - 7 radio / 8 checkbox, is / is not: is the rule's option checked.
 *   - 7 radio, less/greater: FTP compares the rule option's LABEL TEXT, as a
 *     number, with the option ID itself (`label.innerHTML > <optionID>`) — the
 *     selected answer plays no part. Mirrored as is.
 *   - 8 checkbox, less/greater: FTP's label wraps the text in a <div>, so the
 *     number is NaN and the rule never passes. Mirrored as is.
 *   - 16 dropdown: the selected value (option ID, '' when unanswered, '0' for
 *     "other") compared with the rule option ID as strings — so < and > are
 *     string comparisons, as in FTP.
 *   - 17 height/weight: the BMI worked out from `inches|weight` with FTP's
 *     formula (kg/m², ≈ 703 × lb / in²), rounded to 2 places, compared with the
 *     rule text. DELIBERATE DIFFERENCE: FTP compared the BMI as strings
 *     ('9.50' > '30'); we compare numerically when both sides are numbers.
 *   - 9/25/10/14 text, textarea, date: the answer compared with the rule text.
 *     DELIBERATE DIFFERENCE: < and > compare numerically when both sides are
 *     numbers (FTP compared strings, so '9' > '18'); otherwise as strings.
 *   An unanswered source counts as an empty value, as FTP's empty field did.
 */
import type { EFormLogicEntry, EFormLogicRule, EFormQuestion } from './eform.types'

export type EFormAnswers = Record<number, string | string[]>

const CHOICE_TYPES = new Set([7, 8])
const CHECKBOX_TYPE = 8
const DROPDOWN_TYPE = 16
const HEIGHT_WEIGHT_TYPE = 17
const TEXT_TYPES = new Set([9, 25, 10, 14])

/** FTP's `calculateBMI`: kg / m² from whole inches and whole pounds, 2 places; '' when it can't. */
export function bmiFromAnswer(value: string | string[] | undefined): string {
  const str = Array.isArray(value) ? value[0] ?? '' : value ?? ''
  const [heightPart = '', weightPart = ''] = str.split('|')
  const inches = parseInt(heightPart, 10)
  const pounds = parseInt(weightPart, 10)
  const metres = inches / 39.3701
  const bmi = pounds / 2.20462262 / (metres * metres)
  return inches > 0 && Number.isFinite(bmi) ? bmi.toFixed(2) : ''
}

function isNumeric(s: string): boolean {
  return s.trim() !== '' && Number.isFinite(Number(s))
}

/** is / is not / less / greater on two strings; < and > numeric when allowed and both are numbers. */
function compareValues(actual: string, operator: string, expected: string, numericWhenPossible: boolean): boolean {
  switch (operator) {
    case 'is not':
      return actual !== expected
    case 'less_than':
    case 'greater_than': {
      const numeric = numericWhenPossible && isNumeric(actual) && isNumeric(expected)
      const a: string | number = numeric ? Number(actual) : actual
      const b: string | number = numeric ? Number(expected) : expected
      return operator === 'less_than' ? a < b : a > b
    }
    default: // 'is'
      return actual === expected
  }
}

function selectedIds(answer: string | string[] | undefined): string[] {
  if (answer === undefined || answer === '') return []
  return Array.isArray(answer) ? answer : String(answer).split(',')
}

/** One rule's result, or null when FTP would not evaluate it (it leaves the group's flags alone). */
function evaluateRule(rule: EFormLogicRule, answers: EFormAnswers, byOrderId: Map<number, EFormQuestion>): boolean | null {
  if (rule.sourceOrderId === 0 && rule.value === '0') return null
  const source = byOrderId.get(rule.sourceOrderId)
  if (!source) return false // FTP: the source element is missing, so the rule fails
  const answer = answers[rule.sourceOrderId]
  const op = rule.operator
  const ruleText = rule.responseContent ?? ''

  if (CHOICE_TYPES.has(source.questionType)) {
    const option = source.options.find((o) => String(o.id) === rule.value)
    if (op === 'less_than' || op === 'greater_than') {
      if (!option || source.questionType === CHECKBOX_TYPE) return false
      const text = Number(option.text) // JS: label text vs the unquoted option ID
      const id = Number(rule.value)
      return op === 'less_than' ? text < id : text > id
    }
    if (source.options.length > 0 && !option) return false // FTP: no such option element
    const checked = selectedIds(answer).includes(rule.value)
    return op === 'is not' ? !checked : checked
  }

  if (source.questionType === DROPDOWN_TYPE) {
    const raw = Array.isArray(answer) ? answer[0] ?? '' : answer ?? ''
    const value = raw.startsWith('other:') ? '0' : raw
    return compareValues(value, op, rule.value, false)
  }

  if (source.questionType === HEIGHT_WEIGHT_TYPE) {
    return compareValues(bmiFromAnswer(answer), op, ruleText, true)
  }

  if (TEXT_TYPES.has(source.questionType)) {
    const value = Array.isArray(answer) ? answer.join(',') : answer ?? ''
    return compareValues(value, op, ruleText, true)
  }

  return null
}

function groupMet(entry: EFormLogicEntry, answers: EFormAnswers, byOrderId: Map<number, EFormQuestion>): boolean {
  const results = entry.rules
    .map((r) => evaluateRule(r, answers, byOrderId))
    .filter((r): r is boolean => r !== null)
  if (results.length === 0) return false
  return entry.condition === 'any' ? results.some(Boolean) : results.every(Boolean)
}

/** The question's logic groups in the order FTP applies them (a group may appear more than once). */
function groupsInFtpOrder(question: EFormQuestion, allQuestions: EFormQuestion[]): EFormLogicEntry[] {
  const position = new Map(allQuestions.map((q, i) => [q.orderId, i]))
  const applications: { pos: number; ruleId: number; entry: EFormLogicEntry }[] = []
  for (const entry of question.logic) {
    if (entry.action !== 'show' && entry.action !== 'hide') continue
    for (const r of entry.rules) {
      const pos = position.get(r.sourceOrderId)
      if (pos === undefined) continue // FTP only runs logic for questions on the form
      applications.push({ pos, ruleId: r.ruleId, entry })
    }
  }
  applications.sort((x, y) => x.pos - y.pos || x.ruleId - y.ruleId)
  return applications.map((a) => a.entry)
}

/** Whether a question (or page break) is shown, given the answers so far. */
export function isVisible(question: EFormQuestion, answers: EFormAnswers, allQuestions: EFormQuestion[]): boolean {
  if (question.logic.length === 0) return true
  const byOrderId = new Map(allQuestions.map((q) => [q.orderId, q]))
  let visible = true
  for (const entry of groupsInFtpOrder(question, allQuestions)) {
    const met = groupMet(entry, answers, byOrderId)
    visible = entry.action === 'show' ? met : !met
  }
  return visible
}

/** Question types that end a page: 13 page break, 20 mid-form Submit (the renderer ends the form at the first one). */
export const PAGE_BREAK_TYPES = new Set([13, 20])

export interface EFormFormPage {
  /** The page break that opens this page; its logic decides whether the page is shown. Null for the first page. */
  pageBreak: EFormQuestion | null
  /** The page's questions (page breaks left out). */
  questions: EFormQuestion[]
  /** The break's text, shown under the header; '' when the break is just "Page Break" or a type 20. */
  pageTitle: string
}

/**
 * Split the questions into pages at page breaks (types 13 and 20). Each break
 * stays with the page that follows it — in FTP the break's show/hide logic
 * turns that following page on or off (`activePageArray`). An empty page (two
 * breaks in a row, or a break first) is dropped, and the later break wins.
 */
export function splitPages(questions: EFormQuestion[]): EFormFormPage[] {
  const pages: EFormFormPage[] = []
  let current: EFormQuestion[] = []
  let pageBreak: EFormQuestion | null = null

  const pageTitleOf = (q: EFormQuestion | null): string => {
    // A type 20's text is its button's label, never a page title.
    if (q?.questionType === 20) return ''
    const raw = (q?.questionContent || '').replace(/<[^>]*>/g, '').trim()
    return raw.toLowerCase() === 'page break' ? '' : raw
  }

  for (const q of questions) {
    if (PAGE_BREAK_TYPES.has(q.questionType)) {
      if (current.length > 0) {
        pages.push({ pageBreak, questions: current, pageTitle: pageTitleOf(pageBreak) })
        current = []
      }
      pageBreak = q
    } else {
      current.push(q)
    }
  }
  if (current.length > 0 || pages.length === 0) {
    pages.push({ pageBreak, questions: current, pageTitle: pageTitleOf(pageBreak) })
  }
  return pages
}

/**
 * Indexes of the pages the patient walks through, in order: a page is shown
 * when its break is visible (FTP's `activePageArray`). The first page is always
 * shown — FTP always opens on page 1.
 */
export function visiblePageIndexes(pages: EFormFormPage[], answers: EFormAnswers, allQuestions: EFormQuestion[]): number[] {
  const result: number[] = []
  pages.forEach((page, i) => {
    if (i === 0 || !page.pageBreak || isVisible(page.pageBreak, answers, allQuestions)) result.push(i)
  })
  return result
}
